import * as React from "react";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { useI18n } from "../../lib/i18n";
import * as s from "./GuidanceEditorPage.css";

const MAX_ITEMS = 20;
const MAX_LENGTH = 500;

/** Ordered list of short text items (causes, steps, conditions) with add / reorder / remove. */
export function GuidanceStringListEditor({
  id,
  items,
  onChange,
  disabled,
  placeholder,
}: {
  id: string;
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { t } = useI18n();
  const lastRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [focusLast, setFocusLast] = React.useState(false);

  React.useEffect(() => {
    if (focusLast) {
      lastRef.current?.focus();
      setFocusLast(false);
    }
  }, [focusLast, items.length]);

  const update = (index: number, value: string) => onChange(items.map((v, i) => (i === index ? value : v)));
  const remove = (index: number) => onChange(items.filter((_, i) => i !== index));
  const move = (index: number, delta: -1 | 1) => {
    const next = [...items];
    const target = index + delta;
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };

  return (
    <div className={s.field}>
      {items.map((item, i) => (
        <div key={i} className={s.listRow}>
          <span className={s.listIndex} aria-hidden>
            {i + 1}.
          </span>
          <textarea
            id={i === 0 ? id : undefined}
            ref={i === items.length - 1 ? lastRef : undefined}
            className={s.itemTextarea}
            value={item}
            rows={1}
            maxLength={MAX_LENGTH}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={t("pro.system.insights.guidance.itemAria", { index: i + 1 })}
            onChange={(e) => update(i, e.target.value)}
          />
          <div className={s.listButtons}>
            <button
              type="button"
              className={s.iconButton}
              disabled={disabled || i === 0}
              aria-label={t("pro.system.insights.guidance.moveUp")}
              onClick={() => move(i, -1)}
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              className={s.iconButton}
              disabled={disabled || i === items.length - 1}
              aria-label={t("pro.system.insights.guidance.moveDown")}
              onClick={() => move(i, 1)}
            >
              <ArrowDown size={14} />
            </button>
            <button
              type="button"
              className={s.iconButton}
              disabled={disabled}
              aria-label={t("pro.system.insights.guidance.remove")}
              onClick={() => remove(i)}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
      <div>
        <Button
          type="button"
          size="sm"
          color="neutral"
          variant="outline"
          iconLeft={<Plus size={14} />}
          disabled={disabled || items.length >= MAX_ITEMS}
          onClick={() => {
            onChange([...items, ""]);
            setFocusLast(true);
          }}
        >
          {t("pro.system.insights.guidance.addItem")}
        </Button>
      </div>
    </div>
  );
}
